import { RpcClient } from "effect/rpc";
import { FrontDeskKioskPolicyRpcs } from "@ascleo/frontdesk-contracts/kiosk-policy/rpc";
import type { KioskPolicyView } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
import { Cause, Effect, Exit, Option } from "effect";
import { getDocCirrusRpcLayer } from "./bridge-rpc.transport";

/**
 * The decoded result of consulting the read-only kiosk policy RPC.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome: KioskPolicyReadOutcome = { kind: "transport-failure" };
 * ```
 */
export type KioskPolicyReadOutcome =
  | { readonly kind: "current"; readonly view: KioskPolicyView }
  | { readonly kind: "transport-failure" }
  | { readonly kind: "malformed" };

/**
 * App-facing read-only kiosk policy client.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome = await kioskPolicyClient.read();
 * ```
 */
export interface KioskPolicyClient {
  readonly read: () => Promise<KioskPolicyReadOutcome>;
}

async function readKioskPolicy(): Promise<KioskPolicyReadOutcome> {
  const exit = await Effect.runPromiseExit(
    Effect.scoped(
      Effect.gen(function* () {
        const client = yield* RpcClient.make(FrontDeskKioskPolicyRpcs);
        return yield* client.FrontDeskKioskPolicyRead({});
      }),
    ).pipe(Effect.provide(getDocCirrusRpcLayer())),
  );

  return classifyKioskPolicyExit(exit);
}

/**
 * Classifies typed transport failures separately from malformed response defects.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome = classifyKioskPolicyExit(Exit.die("malformed"));
 * ```
 */
export function classifyKioskPolicyExit(
  exit: Exit.Exit<KioskPolicyView, unknown>,
): KioskPolicyReadOutcome {
  if (Exit.isSuccess(exit)) {
    return { kind: "current", view: exit.value };
  }

  const typedFailure = Cause.findErrorOption(exit.cause);
  return Option.isSome(typedFailure)
    ? { kind: "transport-failure" }
    : { kind: "malformed" };
}

/**
 * Shared feature-owned client for current kiosk policy truth.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const result = await kioskPolicyClient.read();
 * ```
 */
export const kioskPolicyClient: KioskPolicyClient = {
  read: readKioskPolicy,
};
